import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BookingStripePayment from '../../components/Profile/BookingStripePayment';
import BookingInvoicePayment from '../../components/Profile/BookingInvoicePayment';
import PaymentBreakdown from '../../components/Common/PaymentBreakdown';
import paymentsApiService, { toCustomerPaymentError } from '../../services/paymentsApi';
import {
  canAccessBooking,
  formatPaymentStatusLabel,
  isApprovedBookingStatus,
  isFreeBooking,
} from '../../utils/bookingPayment';
import {
  formatOrganizerDate,
  formatOrganizerTimeRange,
  getOrganizerBookingBadge,
} from '../../components/OrganizerDashboard/organizerUtils';
import { bookingHeadcount } from '../../utils/bookingHeadcount';
import '../../components/OrganizerDashboard/OrganizerDashboard.css';
import './GuestBookingPaymentPage.css';

const GuestBookingPaymentPage = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paymentToken = (searchParams.get('token') || '').trim();
  const autoPay = searchParams.get('pay') === 'stripe';
  const { currentUser, userProfile, loading: authLoading } = useAuth();

  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const returnUrl = useMemo(() => {
    if (!bookingId || !paymentToken) return null;
    const params = new URLSearchParams({ token: paymentToken });
    if (autoPay) params.set('pay', 'stripe');
    return `${window.location.origin}/pay/booking/${encodeURIComponent(bookingId)}?${params.toString()}`;
  }, [bookingId, paymentToken, autoPay]);

  const bookingStub = useMemo(() => {
    if (!checkout) return null;
    const eligibility = checkout.eligibility || {};
    return {
      id: checkout.booking_id,
      bookingReference: checkout.booking_reference,
      status: checkout.booking_status,
      contactEmail: checkout.organizer_email,
      paymentStatus: checkout.payment_status,
      freeBooking: checkout.free_booking,
      totalPrice: eligibility.charge_total ?? eligibility.amount ?? 0,
      processingFee: eligibility.processing_fee ?? 0,
      platformFee: eligibility.platform_fee ?? 0,
      amountPending: eligibility.charge_total ?? eligibility.amount ?? 0,
    };
  }, [checkout]);

  const hasFullAccountAccess = useMemo(() => {
    if (!bookingStub || !currentUser) return false;
    return canAccessBooking(bookingStub, { userProfile, currentUser });
  }, [bookingStub, currentUser, userProfile]);

  useEffect(() => {
    if (authLoading || loading || !checkout || !hasFullAccountAccess) return;
    const params = new URLSearchParams();
    if (autoPay) params.set('pay', 'stripe');
    const query = params.toString();
    navigate(`/profile/bookings/${bookingId}${query ? `?${query}` : ''}`, { replace: true });
  }, [authLoading, loading, checkout, hasFullAccountAccess, autoPay, bookingId, navigate]);

  useEffect(() => {
    if (!bookingId || !paymentToken) {
      setLoading(false);
      if (!paymentToken) {
        setError('This link is missing its secure access token. Open the link from your booking email.');
      }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        paymentsApiService.setPaymentToken(paymentToken);
        const data = await paymentsApiService.getGuestCheckout(bookingId);
        if (!cancelled) setCheckout(data);
      } catch (loadError) {
        if (!cancelled) {
          setError(toCustomerPaymentError(loadError?.message));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      paymentsApiService.clearPaymentToken();
    };
  }, [bookingId, paymentToken]);

  const handleDownloadInvoice = async () => {
    if (!checkout?.invoice_file_id || !bookingId) return;
    try {
      setDownloadingInvoice(true);
      paymentsApiService.setPaymentToken(paymentToken);
      const response = await paymentsApiService.getGuestInvoiceDownloadUrl(
        bookingId,
        checkout.invoice_file_id
      );
      if (response?.download_url) {
        window.open(response.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch (downloadError) {
      setError(toCustomerPaymentError(downloadError?.message) || 'Could not download the invoice.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleSignIn = () => {
    window.dispatchEvent(new CustomEvent('openLoginModal'));
  };

  if (authLoading || (hasFullAccountAccess && checkout)) {
    return (
      <div className="guest-booking-payment">
        <div className="guest-booking-payment__card">
          <p className="guest-booking-payment__meta">Loading your booking…</p>
        </div>
      </div>
    );
  }

  if (!paymentToken) {
    return (
      <div className="guest-booking-payment">
        <div className="guest-booking-payment__card">
          <p className="guest-booking-payment__eyebrow">Booking access</p>
          <h1>Link not valid</h1>
          <p className="guest-booking-payment__lead">{error}</p>
          <button type="button" className="guest-booking-payment__cta" onClick={handleSignIn}>
            Sign in to Occasia
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="guest-booking-payment">
        <div className="guest-booking-payment__card">
          <p className="guest-booking-payment__meta">Loading your booking…</p>
        </div>
      </div>
    );
  }

  if (error && !checkout) {
    return (
      <div className="guest-booking-payment">
        <div className="guest-booking-payment__card">
          <p className="guest-booking-payment__eyebrow">Booking access</p>
          <h1>Unable to open booking</h1>
          <p className="guest-booking-payment__lead">{error}</p>
          <p className="guest-booking-payment__lead">
            Try opening the link from your latest booking email, or sign in with the email address
            you used when booking.
          </p>
          <button type="button" className="guest-booking-payment__cta" onClick={handleSignIn}>
            Sign in to Occasia
          </button>
        </div>
      </div>
    );
  }

  const eligibility = checkout?.eligibility || {};
  const paymentMode = checkout?.payment_mode || 'invoice';
  const statusBadge = getOrganizerBookingBadge(bookingStub);
  const checkoutGuests = bookingHeadcount(checkout);
  const venueApproved = isApprovedBookingStatus(checkout?.booking_status);
  const needsPayment =
    venueApproved &&
    !isFreeBooking(bookingStub) &&
    !['paid', 'completed', '1'].includes(String(checkout?.payment_status || '').toLowerCase());

  return (
    <div className="guest-booking-payment">
      <div className="guest-booking-payment__card">
        <p className="guest-booking-payment__eyebrow">Your booking</p>
        <h1>{checkout?.venue_name || 'Venue booking'}</h1>
        {checkout?.venue_address ? (
          <p className="guest-booking-payment__meta">{checkout.venue_address}</p>
        ) : null}

        <div className="guest-booking-payment__signin-banner">
          <p>
            Viewing as guest.{' '}
            <button type="button" className="guest-booking-payment__link-button" onClick={handleSignIn}>
              Sign in with {checkout?.organizer_email || 'your booking email'}
            </button>{' '}
            to message the venue and manage everything in your profile.
          </p>
        </div>

        {error ? <p className="guest-booking-payment__error">{error}</p> : null}

        <div className="guest-booking-payment__summary">
          <div className="guest-booking-payment__summary-header">
            <h2>{checkout?.event_activity || 'Event'}</h2>
            {statusBadge ? (
              <span className={`org-badge org-badge--${statusBadge.variant}`}>{statusBadge.label}</span>
            ) : null}
          </div>
          <ul className="guest-booking-payment__facts">
            <li>
              <span>Reference</span>
              <strong>{checkout?.booking_reference || '—'}</strong>
            </li>
            <li>
              <span>Date</span>
              <strong>{formatOrganizerDate(checkout?.booking_date)}</strong>
            </li>
            <li>
              <span>Time</span>
              <strong>{formatOrganizerTimeRange(checkout?.start_time, checkout?.end_time)}</strong>
            </li>
            {checkoutGuests ? (
              <li>
                <span>Guests</span>
                <strong>{checkoutGuests}</strong>
              </li>
            ) : null}
            <li>
              <span>Organiser</span>
              <strong>{checkout?.organizer_name || checkout?.organizer_email || '—'}</strong>
            </li>
          </ul>
        </div>

        {venueApproved && !isFreeBooking(bookingStub) && eligibility.charge_total > 0 ? (
          <div className="guest-booking-payment__summary">
            <h2>Price summary</h2>
            <PaymentBreakdown
              subtotal={eligibility.subtotal ?? 0}
              platformFee={eligibility.platform_fee ?? 0}
              processingFee={eligibility.processing_fee ?? 0}
              chargeTotal={eligibility.charge_total}
            />
          </div>
        ) : null}

        <div className="guest-booking-payment__summary">
          <h2>Payment</h2>
          {isFreeBooking(bookingStub) ? (
            <p className="guest-booking-payment__lead">No payment is required for this booking.</p>
          ) : !venueApproved ? (
            <p className="guest-booking-payment__lead">
              Payment will be available once the venue approves your booking.
            </p>
          ) : paymentMode === 'occasia_stripe' && needsPayment ? (
            <BookingStripePayment
              bookingId={bookingId}
              booking={bookingStub}
              bookingStatus={checkout?.booking_status}
              autoOpen={autoPay}
              paymentToken={paymentToken}
              returnUrl={returnUrl}
            />
          ) : paymentMode === 'external_link' && needsPayment && checkout?.external_payment_link ? (
            <a
              className="guest-booking-payment__cta guest-booking-payment__cta--block"
              href={checkout.external_payment_link}
              target="_blank"
              rel="noreferrer"
            >
              Complete payment
            </a>
          ) : paymentMode === 'invoice' && needsPayment ? (
            <BookingInvoicePayment
              amount={eligibility.charge_total}
              paymentStatus={checkout?.payment_status || eligibility.payment_status}
              hasInvoice={checkout?.has_ready_invoice}
              onDownloadInvoice={handleDownloadInvoice}
              downloading={downloadingInvoice}
            />
          ) : (
            <p className="guest-booking-payment__lead">
              Payment status:{' '}
              <strong>{formatPaymentStatusLabel(checkout?.payment_status || 'pending')}</strong>
            </p>
          )}
        </div>

        <p className="guest-booking-payment__footer-note">
          This secure link is tied to booking <strong>{checkout?.booking_reference}</strong> and can
          only be used for this booking.
        </p>
      </div>
    </div>
  );
};

export default GuestBookingPaymentPage;
